import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TbArrowLeft, TbSearch, TbFileText, TbSparkles } from "react-icons/tb";
import { useTemplates } from "../../hooks/useTemplates";
import { normalizeTemplateId } from "../../../services/templateService";
import { schemaVersionLabel } from "../../../utils/scanSchemaVersion";
import { Button, Alert, EmptyState } from "../../components";
import "../../styles/tokens.css";
import "./Reports.css";

// Start a report: pick a template.
//
// The single step of what used to be a two-step wizard. You arrive here by clicking
// Report on a specific scan, so the data source is already decided — the old step one
// re-fetched the whole scan list purely to re-select the file path it had just been
// handed, and asked which scan you meant immediately after you said which scan you meant.
//
// v1 keeps its own two-step page at /report-builder/template-selection. This is a
// separate route so that one is untouched.
//
// The state this hands the builder is the only contract that matters. ReportBuilderPage
// reads exactly: mode, templateId, fileUrl, internalScanPath, externalScanPath,
// hasInternalData, hasExternalData. Nothing else it is given is read.

// The two ways to start without a stored template. They lead the grid rather than
// trailing it: both are a decision about HOW to build the report, which comes before
// picking which of the saved layouts to use.
const BLANK = {
  id: "blank",
  name: "Blank layout",
  description: "Start with an empty report and add blocks yourself.",
};
const AI = {
  id: "ai",
  name: "Generate with AI",
  description: "Let the builder draft a report from this scan, then edit what it produces.",
};

const viewsFor = (isPro) => [
  { key: "yours", label: "Yours" },
  isPro ? { key: "pro", label: "Pro defaults" } : { key: "community", label: "Community defaults" },
  { key: "all", label: "All" },
];

const ReportTemplatePicker = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { internalScanPath, externalScanPath, scanLabel, scanDate } = location.state || {};

  // Templates are tied to the scan JSON format they were built for, so only those
  // matching this scan's scanner major version are listed (see utils/scanSchemaVersion).
  const { groups, loading, error, isPro, scanSchemaVersion, hiddenCount } =
    useTemplates(internalScanPath || externalScanPath || null);
  const [view, setView] = useState("yours");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const views = useMemo(() => viewsFor(isPro), [isPro]);

  const rows = useMemo(() => {
    const list = groups[view] || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) =>
      (t.name || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q));
  }, [groups, view, search]);

  // A pro default is shown, not hidden, for a community tenant — the same rule the
  // template hub applies. Seeing what an upgrade buys is the point.
  const locked = (t) => t.is_pro_default && !isPro;

  // Takes the template explicitly so a double click can open the tile it landed on
  // without waiting for the setSelected from the same event to be applied.
  const open = (tpl = selected) => {
    if (!tpl || locked(tpl)) return;
    // Neither of the two lead tiles is a stored template, so neither carries an id.
    const synthetic = tpl.id === "blank" || tpl.id === "ai";
    const templateId = synthetic ? null : normalizeTemplateId(tpl);
    navigate("/report-builder", {
      state: {
        // fileUrl is the field the builder actually fetches metrics with; the two
        // *ScanPath fields tell it which sides are present.
        fileUrl: internalScanPath || externalScanPath || null,
        internalScanPath: internalScanPath || null,
        externalScanPath: externalScanPath || null,
        templateId,
        mode: "report",
        hasInternalData: !!internalScanPath,
        hasExternalData: !!externalScanPath,
        // The builder opens its AI panel on arrival when this is set. Same flag the old
        // wizard's "Generate with AI" used, so the builder needs no change.
        autoOpenAI: tpl.id === "ai",
      },
    });
  };

  // Nobody should be able to reach this screen without a scan. If they do — a refresh
  // drops router state — say so plainly rather than opening a builder with no data.
  if (!internalScanPath && !externalScanPath) {
    return (
      <div className="v2 v2-reportpick">
        <div className="v2-reportpick-body">
          <EmptyState title="No scan to report on"
            action={<Button variant="primary" style={{ marginTop: ".75rem" }} onClick={() => navigate("/organizations")}>Back to organizations</Button>}>
            Start a report from the Report button on a scan, so the report knows which scan
            it is for.
          </EmptyState>
        </div>
      </div>
    );
  }

  return (
    <div className="v2 v2-reportpick">
      <div className="v2-reportpick-top">
        <div className="v2-crumbs">
          <span>Reports</span><span aria-hidden="true">/</span><b>New report</b>
        </div>
        <div className="v2-omni" style={{ marginLeft: "auto" }}>
          <TbSearch size={14} />
          <input type="search" value={search} placeholder="Search templates"
                 aria-label="Search templates" onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* The source is a statement, not a choice. There is no dismiss on the chip because
          you cannot remove the only source you arrived with. */}
      <div className="v2-reportpick-source">
        <span className="v2-reportpick-for">Report for</span>
        <span className="v2-chip">
          {internalScanPath ? "Internal" : "External"} <b>{scanLabel || "Selected scan"}</b>
        </span>
        {scanDate && <span className="v2-reportpick-date">Scanned {scanDate}</span>}
        {scanSchemaVersion && (
          <span className="v2-reportpick-date" title="Only templates built for this scan format are listed">
            Scanner {schemaVersionLabel(scanSchemaVersion)}
          </span>
        )}
      </div>

      <div className="v2-views">
        {views.map((v) => (
          <button key={v.key} type="button"
                  className={`v2-view${view === v.key ? " on" : ""}`}
                  aria-current={view === v.key ? "page" : undefined}
                  onClick={() => setView(v.key)}>
            {v.label}<span className="n">{(groups[v.key] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="v2-reportpick-body">
        <Alert>{error}</Alert>

        {!loading && hiddenCount > 0 && (
          <p className="v2-hint">
            {hiddenCount} template{hiddenCount === 1 ? " is" : "s are"} hidden because{" "}
            {hiddenCount === 1 ? "it was" : "they were"} built for a different scanner version
            than this {schemaVersionLabel(scanSchemaVersion)} scan.
          </p>
        )}

        {loading ? <p className="v2-hint">Loading templates…</p> : (
          <div className="v2-tplpick">
            <button type="button"
                    className={`v2-tplitem v2-tplitem--lead${selected?.id === "ai" ? " on" : ""}`}
                    aria-pressed={selected?.id === "ai"}
                    onClick={() => setSelected(AI)}
                    onDoubleClick={() => open(AI)}>
              <TbSparkles size={18} />
              <span className="v2-tplitem-name" style={{ paddingRight: 0 }}>{AI.name}</span>
              <span className="v2-tplitem-desc">{AI.description}</span>
            </button>

            <button type="button"
                    className={`v2-tplitem v2-tplitem--lead${selected?.id === "blank" ? " on" : ""}`}
                    aria-pressed={selected?.id === "blank"}
                    onClick={() => setSelected(BLANK)}
                    onDoubleClick={() => open(BLANK)}>
              <TbFileText size={18} />
              <span className="v2-tplitem-name" style={{ paddingRight: 0 }}>{BLANK.name}</span>
              <span className="v2-tplitem-desc">{BLANK.description}</span>
            </button>

            {rows.map((t) => (
              <button key={t.id} type="button"
                      className={`v2-tplitem${selected?.id === t.id ? " on" : ""}${locked(t) ? " locked" : ""}`}
                      aria-pressed={selected?.id === t.id}
                      disabled={locked(t)}
                      title={locked(t) ? "Available on Pro" : undefined}
                      onClick={() => setSelected(t)}
                      onDoubleClick={() => open(t)}>
                {(selected?.id === t.id || locked(t)) && (
                  <span className="v2-tplitem-badge">
                    <span className={`v2-rbadge ${locked(t) ? "v2-rbadge--warning" : "v2-rbadge--accent"}`}>
                      {locked(t) ? "PRO" : "SELECTED"}
                    </span>
                  </span>
                )}
                <span className="v2-tplitem-name">{t.name}</span>
                <span className="v2-tplitem-desc">{t.description || "No description."}</span>
              </button>
            ))}

          </div>
        )}

        {!loading && rows.length === 0 && search && (
          <EmptyState title="No matches">Nothing matches "{search}".</EmptyState>
        )}
      </div>

      <div className="v2-reportpick-foot">
        <Button onClick={() => navigate(-1)}><TbArrowLeft size={15} /> Back</Button>
        <Button variant="primary" style={{ marginLeft: "auto" }} disabled={!selected} onClick={() => open()}>
          {!selected ? "Choose a template"
            : selected.id === "ai" ? "Generate with AI"
            : "Open in builder"}
        </Button>
      </div>
    </div>
  );
};

export default ReportTemplatePicker;
